<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { formatSnakeCaseToTitle, valueOutOfRange } from 'src/helpers/utilities';
	import {
		convertSeconds,
		convertToSeconds,
		formatTimeLabel,
		formatTwoDigits,
		getTimeWheelMsStep,
		getTimeWheelTexts,
		getTimeWheelToggleText,
	} from 'src/helpers/timer_helpers';
	//Design system components
	import { Checkbox, Container, Text, TimeWheel } from '@Shelly/shelly-design-system';
	//Types
	import type { PeripheralSwitchSettings } from 'src/types/Peripherals';
	import { onMount } from 'svelte';

	let className = '';
	export { className as class };

	export let settings: PeripheralSwitchSettings,
		error = false;

	let isMount = false;
	let subsettings = ['auto_on', 'auto_off'];
	const min = 0.01;
	const max = 2147483;

	let selectedHour = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMinute = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedSecond = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMs = { auto_on_delay: undefined, auto_off_delay: undefined };
	let maxTimes = convertSeconds(max);

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: checkBoxInfoText = {
		auto_on: `${$format('auto_on_description')} 
					<span class='text-primary text-bold'>
						${formatTimeLabel(
									selectedHour.auto_on_delay,
									selectedMinute.auto_on_delay,
									selectedSecond.auto_on_delay,
									selectedMs.auto_on_delay,
								)}
					</span>`,
		auto_off: `${$format('auto_off_description')}
					<span class='text-primary text-bold'>
						${formatTimeLabel(
									selectedHour['auto_off_delay'],
									selectedMinute['auto_off_delay'],
									selectedSecond['auto_off_delay'],
									selectedMs['auto_off_delay'],
								)}
					</span>`,
	};

	$: errorsText = {
		auto_on_error: valueOutOfRange(+settings.auto_on_delay, min, max) ? infoText : '',
		auto_off_error: valueOutOfRange(+settings.auto_off_delay, min, max) ? infoText : '',
	};

	$: error =
		valueOutOfRange(+settings.auto_on_delay, min, max) ||
		valueOutOfRange(+settings.auto_off_delay, min, max);

	$: if (isMount) updateOutValues(selectedHour, selectedMinute, selectedSecond, selectedMs);

	onMount(() => {
		updateTimeValues('auto_on_delay');
		updateTimeValues('auto_off_delay');
		isMount = true;
	});

	const updateOutValues = (hour, minute, second, ms) => {
		setTimeout(() => {
			settings = {
				...settings,
				auto_on_delay: `${getTimeInSeconds('auto_on_delay')}`,
				auto_off_delay: `${getTimeInSeconds('auto_off_delay')}`,
			};
		});
	};

	const updateTimeValues = (type: 'auto_on_delay' | 'auto_off_delay') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(settings[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const getTimeInSeconds = (type: 'auto_on_delay' | 'auto_off_delay') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};
</script>

<div class={className}>
	<Text styleType="h2" class="mb-s">
		{$format('settings_title_timer', {
			default: 'Timer',
		})}
	</Text>

	{#each subsettings as setting, index}
		<Container
			type="modalContainer"
			padding="xs"
			class={`${index < subsettings.length - 1 ? 'mb-s' : ''} border-color-transparent`}
		>
			{#key checkBoxInfoText}
				<Checkbox
					class="mb-s"
					size="m"
					info={checkBoxInfoText[setting]}
					bind:checked={settings[setting]}
					id="checkbox-cbos9eo"
				>
					{formatSnakeCaseToTitle($format(setting))}
				</Checkbox>
			{/key}
			<TimeWheel
				id="timewheel-5c87zbq"
				a11yProps={{
					secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
						default: 'Time wheel seconds column',
					}),
					secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
						default: 'Time wheel second prefix',
					}),
					msColumnAriaLabel: $format('label_time_wheel_ms_column', {
						default: 'Time wheel ms column',
					}),
					msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
						default: 'Time wheel ms prefix',
					}),
					inputAriaLabel: $format('label_time_wheel_input', { default: 'Time wheel input' }),
					buttonAriaLabel: $format('label_time_wheel_button', {
						default: 'Time wheel button',
					}),
					hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
						default: 'Time wheel hours column',
					}),
					hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
						default: 'Time wheel hour prefix',
					}),
					minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
						default: 'Time wheel minutes column',
					}),
					minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
						default: 'Time wheel minute prefix',
					}),
				}}
				class="mb-s"
				bind:selectedHour={selectedHour[`${setting}_delay`]}
				bind:selectedMinute={selectedMinute[`${setting}_delay`]}
				bind:selectedSecond={selectedSecond[`${setting}_delay`]}
				bind:selectedMs={selectedMs[`${setting}_delay`]}
				msToggleText={getTimeWheelToggleText()}
				maxHours={maxTimes.hours}
				info={infoText}
				error={errorsText[`${setting}_error`]}
				texts={getTimeWheelTexts()}
				msStep={getTimeWheelMsStep()}
			/>
		</Container>
	{/each}
</div>
